/* eslint-disable @typescript-eslint/no-explicit-any */
import { gql as rawGql } from "../__generated__";
import type { Documents } from "../__generated__/gql";
import { TypedDocumentString } from "../__generated__/graphql";

function isKnownSource(source: string): source is keyof Documents {
  return rawGql(source as any) instanceof TypedDocumentString;
}

/**
 * This is a document that is theoretically valid and will be accepted by a GraphQL
 * server, but it is not typed. This is used when the query is not known at compile time.
 */
type UnknownTypedDocumentString = TypedDocumentString<any, any>;

/**
 * This is a layer between the generated `gql` function and the
 * one we want to use. This allows queries to still be used
 * in production when building with an empty schema, as it will
 * create a `TypedDocumentString` with the query string on the fly.
 *
 * (graphql-codegen returns empty objects for unknown queries otherwise)
 *
 * This could likely be improved but we don't want to require the server
 * to be up to date before the backend, or to have all schemas downloaded
 * before the frontend can be built.
 *
 * We can instead use this and an empty schema to create a minimal graphql build.
 */

export default function gql<const T extends string>(source: T):
Documents extends Record<string, never>
  // In prod builds (no overrides therefore params are generic), we return any doc.
  ? UnknownTypedDocumentString
  : T extends keyof Documents
    ? Documents[T]
    // If this is truly an invalid document, we get here.
    : UnknownTypedDocumentString {
  // This if statement is needed to satisfy rawGql's dev-time requirement that the
  // query satisfies one of the overrides.
  if (isKnownSource(source)) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore Funky types here
    return rawGql(source) as any;
  }

  // Make it on the fly.
  return new TypedDocumentString<any, any>(source) as any;
}
